import type { KeyboardEvent, ReactNode } from 'react'

export interface PhotoGridItem {
  id: string
  src: string
  label: string
}

interface PhotoGridProps {
  items: PhotoGridItem[]
  onItemClick?: (item: PhotoGridItem, index: number) => void
  viewMode?: 'small' | 'medium'
  renderOverlay?: (item: PhotoGridItem, index: number) => ReactNode
}

// Presentational only: no fetching, no selection, no upload awareness.
// renderOverlay exists for exactly this kind of reuse, but SOL-150 evaluated
// adopting this for the owner grid (PhotoGallery) and deferred it: viewMode
// here is 'small' | 'medium' where the gallery needs four modes, src is a
// single string where the gallery needs a three-way existing/preview/
// placeholder branch, and adopting it would add role="button"/tabIndex/
// aria-label to owner tiles -- a behaviour change out of scope for that
// branch. PhotoGallery ships its own markup instead.
export default function PhotoGrid({
  items,
  onItemClick,
  viewMode = 'small',
  renderOverlay,
}: PhotoGridProps) {
  // A clickable tile must also be reachable and operable from the keyboard
  // (WCAG 2.1.1) — on the public view page opening the lightbox is the only
  // interaction there is. A tile with no click handler gets none of this: a
  // non-interactive element must not be focusable or announced as a button.
  const interactive = Boolean(onItemClick)
  const activate = (item: PhotoGridItem, index: number) => onItemClick?.(item, index)
  const onKeyDown = (item: PhotoGridItem, index: number) => (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter' && e.key !== ' ') return
    // Space would otherwise scroll the page out from under the lightbox.
    e.preventDefault()
    activate(item, index)
  }

  return (
    <div className={`photo-grid photo-grid--${viewMode}`}>
      {items.map((item, index) => (
        <div
          key={item.id}
          className="photo-item"
          onClick={interactive ? () => activate(item, index) : undefined}
          onKeyDown={interactive ? onKeyDown(item, index) : undefined}
          role={interactive ? 'button' : undefined}
          tabIndex={interactive ? 0 : undefined}
          aria-label={interactive ? item.label : undefined}
        >
          <img src={item.src} alt={item.label} loading="lazy" />
          {renderOverlay?.(item, index)}
        </div>
      ))}
    </div>
  )
}
